import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import type { ExportTableColumn } from '../../../utils/exportUtils';

type Cell = string | number | boolean | null | undefined;

interface TemplateReportDialogProps {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  columns: ExportTableColumn[];
  rows: Array<Record<string, Cell>>;
  onClose: () => void;
  onExportCSV: () => void;
  onExportExcel: () => void;
  onExportPDF: () => void;
}

const TemplateReportDialog = ({
  isOpen,
  title,
  subtitle,
  columns,
  rows,
  onClose,
  onExportCSV,
  onExportExcel,
  onExportPDF,
}: TemplateReportDialogProps) => {
  if (!isOpen) return null;

  const previewRows = rows.slice(0, 50);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
      <div className="bg-card rounded-md shadow-elevation-2 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="FileText" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">{title}</h3>
                {subtitle ? (
                  <p className="text-sm text-muted-foreground">{subtitle}</p>
                ) : null}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-sm hover:bg-muted transition-interactive"
              aria-label="Close"
            >
              <Icon name="X" size={20} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-auto">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              Showing {previewRows.length.toLocaleString()} of {rows.length.toLocaleString()} rows
            </p>
            <div className="flex gap-2">
              <Button variant="outline" iconName="FileDown" iconPosition="left" onClick={onExportCSV}>
                CSV
              </Button>
              <Button variant="outline" iconName="FileSpreadsheet" iconPosition="left" onClick={onExportExcel}>
                Excel
              </Button>
              <Button variant="default" iconName="FileText" iconPosition="left" onClick={onExportPDF}>
                PDF
              </Button>
            </div>
          </div>

          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} className="text-left px-3 py-2 font-semibold text-foreground whitespace-nowrap">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, idx) => (
                  <tr key={idx} className="border-t border-border">
                    {columns.map((c) => (
                      <td key={c.key} className="px-3 py-2 text-foreground whitespace-nowrap">
                        {row[c.key] == null ? '' : String(row[c.key])}
                      </td>
                    ))}
                  </tr>
                ))}
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-3 py-6 text-center text-muted-foreground">
                      No data for this period/filters.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="p-6 border-t border-border flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TemplateReportDialog;


